/* eslint-disable max-lines-per-function */
import { useEffect, useMemo, useRef, useState } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { useAuth } from '../../../../Hooks/useAuth';
import AllocationMisToolbar from './AllocationMisToolbar';
import { ALLOCATION_MIS } from '../../../../Api/resources/endPoint';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../../Api/resources/serviceURLs';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../../Delivery/utils';
import {
  decorateAllocationMisRows,
  getAllocationMisCellValue,
  preserveAllocationMisServerOrder,
  transformAllocMisExport,
} from '../utils/allocationMisTransformers';
import '../AllocationMis.scss';

const PAGE_SIZE = 10;
const STATIC_ALLOCATION_FIELDS = new Set([
  'section',
  'employeeName',
  'employeeId',
  'employeeVertical',
  'emailId',
  'employeeDesignation',
  'employeeDepartment',
  'projectName',
  'projectId',
  'projectVertical',
  'resourceCategory',
  'tbhId',
  'resourceType',
  'numberOfDays',
  'totalDays',
  'employeeType',
  'employeeExperience',
  'skills',
  'totalEfforts',
  'resourceAllocationComment',
  'rowType',
]);
const BENCH_ROW_STYLE = { backgroundColor: 'var(--allocation-mis-bench-row-bg)' };

const buildProjectServiceApiUrl = (path) => {
  if (!path) return '';

  const servicePath = appendServiceNameToURL(serviceName.PROJECT_MS);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${servicePath}/api${path}`;
  }

  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  return `${process.env.REACT_APP_API_URL}:${servicePort}${servicePath}/api${path}`;
};

const baseURL = buildProjectServiceApiUrl(ALLOCATION_MIS);

const isOverAllocatedValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 100;
};

const OVER_ALLOCATION_TEXT_COLOR = 'var(--allocation-mis-over-color)';
const OVER_ALLOCATION_FONT_WEIGHT = 'var(--allocation-mis-over-font-weight)';
const BENCH_CELL_CLASS_NAME = 'allocation-mis-cell--bench';
const OVER_ALLOCATION_CELL_CLASS_NAME = 'allocation-mis-cell--over';
const NO_DIVIDER_CELL_CLASS_NAME = 'allocation-mis-cell--no-divider';
const TOTAL_CELL_CLASS_NAME = 'allocation-mis-cell--total';
const joinCellClassNames = (...classNames) => classNames.filter(Boolean).join(' ');

const getAllocationMisResponseData = (response) => {
  const payload = response?.data;
  if (!payload || typeof payload !== 'object') return response;

  return payload?.data && typeof payload.data === 'object' ? payload.data : payload;
};

const getAllocationMisResponseMeta = (response) => (
  response?.meta
  || response?.data?.meta
  || response?.data?.data?.meta
  || null
);

const getAllocationMisSortSignature = (tableConfiguration = []) => {
  if (!Array.isArray(tableConfiguration)) return '';

  const activeSortItem = tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .find((item) => (
      item?.type === 'sort'
      && (item?.sortOrder === 1
        || item?.sortOrder === -1
        || item?.sortDirection === 'Asc'
        || item?.sortDirection === 'Desc')
    ));

  if (!activeSortItem) return '';

  return [
    activeSortItem.field,
    activeSortItem.sortOrder,
    activeSortItem.sortDirection,
  ].map((value) => `${value ?? ''}`).join('|');
};

const hasAllocationMisPayloadSort = (payload = {}) => (
  Array.isArray(payload?.filter)
  && payload.filter.some((item) => item?.sortDirection === 'Asc' || item?.sortDirection === 'Desc')
);

const getAllocationMisCellClassName = (params = {}) => {
  const row = params?.row || {};
  const existingClassName = params?.colDef?.cellClassName;

  const classes = [];

  if (row?.rowType === 'project' && `${row?.projectName || ''}`.trim().toLowerCase() === 'bench') {
    classes.push(BENCH_CELL_CLASS_NAME);
  }

  if (
    row?.rowType === 'total'
    && !STATIC_ALLOCATION_FIELDS.has(params?.field)
    && params?.field !== 'allocationPercent'
    && isOverAllocatedValue(row?.[params?.field])
  ) {
    classes.push(OVER_ALLOCATION_CELL_CLASS_NAME);
  }

  if (row?.rowType === 'total') {
    classes.push(TOTAL_CELL_CLASS_NAME);
  }

  if (row?.allocationMisDisplay?.hideTableLine) {
    classes.push(NO_DIVIDER_CELL_CLASS_NAME);
  }

  return joinCellClassNames(
    typeof existingClassName === 'string' ? existingClassName : '',
    ...classes,
  );
};

const buildOverAllocationTemplateEntries = (row = {}) => {
  const entries = [];

  if (isOverAllocatedValue(row?.allocationPercent)) {
    entries.push({
      field: 'allocationPercent',
      textColor: OVER_ALLOCATION_TEXT_COLOR,
      fontWeight: OVER_ALLOCATION_FONT_WEIGHT,
    });
  }

  if (row?.rowType === 'total') {
    Object.keys(row).forEach((field) => {
      if (!STATIC_ALLOCATION_FIELDS.has(field) && field !== 'allocationPercent' && isOverAllocatedValue(row[field])) {
        entries.push({ field, textColor: OVER_ALLOCATION_TEXT_COLOR, fontWeight: OVER_ALLOCATION_FONT_WEIGHT });
      }
    });
  }

  return entries;
};

const decorateAllocationMisResponse = (response) => {
  const payload = response?.data;
  if (!payload || typeof payload !== 'object') return response;

  const sourceData = getAllocationMisResponseData(response);
  const { tableConfiguration, tableData } = preserveAllocationMisServerOrder(
    sourceData.tableConfiguration,
    decorateAllocationMisRows(sourceData.tableData),
  );

  const decoratedRows = tableData.map((row) => {
    const isBenchRow = row?.rowType === 'project' && `${row?.projectName || ''}`.trim().toLowerCase() === 'bench';
    const overAllocationEntries = buildOverAllocationTemplateEntries(row);

    return {
      ...row,
      ...(isBenchRow ? { style: { ...(row.style || {}), ...BENCH_ROW_STYLE } } : {}),
      ...(overAllocationEntries.length > 0 ? {
        rowConfiguration: {
          ...(row.rowConfiguration || {}),
          template: [...(row.rowConfiguration?.template || []), ...overAllocationEntries],
        },
      } : {}),
    };
  });

  const decoratedColumns = tableConfiguration.map((column) => ({
    ...column,
    ...(column?.field
      ? {
        renderCell: (params) => getAllocationMisCellValue(params?.row, column.field, params?.value, column.dateFormat),
        cellClassName: getAllocationMisCellClassName,
      }
      : {}),
  }));

  sourceData.tableConfiguration = decoratedColumns;
  sourceData.tableData = decoratedRows;
  sourceData.rows = decoratedRows;
  payload.tableConfiguration = decoratedColumns;
  payload.tableData = decoratedRows;
  payload.rows = decoratedRows;
  response.tableConfiguration = decoratedColumns;
  response.tableData = decoratedRows;
  response.rows = decoratedRows;

  if (payload?.data && typeof payload.data === 'object') {
    payload.data.tableConfiguration = decoratedColumns;
    payload.data.tableData = decoratedRows;
    payload.data.rows = decoratedRows;
  }

  return response;
};

const AllocationMis = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const tableStateHandlerRef = useRef(null);
  const currentPaginationRef = useRef({ page: 1, limit: PAGE_SIZE, total: 0 });
  const lastSortSignatureRef = useRef('');
  const paginationClickInFlightRef = useRef(false);
  const [tableInstanceKey, setTableInstanceKey] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResetOrFilterClick = (event) => {
      const toolbarButton = event.target.closest('button');
      const isPaginatorClick = Boolean(event.target.closest('.p-paginator'));

      const isToolbarReset = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(toolbarButton?.querySelector('.pi-refresh'));

      if (isPaginatorClick && container.contains(event.target)) {
        paginationClickInFlightRef.current = true;
      }

      if (!isToolbarReset) return;
      if (!container.contains(event.target)) return;

      currentPaginationRef.current = { page: 1, limit: PAGE_SIZE, total: 0 };
      lastSortSignatureRef.current = '';
      paginationClickInFlightRef.current = false;
      setTableInstanceKey((prev) => prev + 1);
    };

    container.addEventListener('click', handleResetOrFilterClick, true);

    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function open(method, url, ...args) {
      this.__allocationMisRequestUrl = url;
      return originalOpen.call(this, method, url, ...args);
    };

    window.XMLHttpRequest.prototype.send = function send(body) {
      const requestUrl = `${this.__allocationMisRequestUrl || ''}`;

      if (requestUrl.includes(ALLOCATION_MIS) && typeof body === 'string') {
        try {
          const payload = JSON.parse(body);
          const currentPage = Number(currentPaginationRef.current?.page) || 1;
          const shouldPreservePage = currentPage > 1
            && Number(payload?.pagination?.page) === 1
            && hasAllocationMisPayloadSort(payload)
            && !paginationClickInFlightRef.current;

          if (shouldPreservePage) {
            return originalSend.call(this, JSON.stringify({
              ...payload,
              pagination: {
                ...payload.pagination,
                page: currentPage,
              },
            }));
          }
        } catch (error) {
          return originalSend.call(this, body);
        }
      }

      return originalSend.call(this, body);
    };

    return () => {
      container.removeEventListener('click', handleResetOrFilterClick, true);
      window.XMLHttpRequest.prototype.open = originalOpen;
      window.XMLHttpRequest.prototype.send = originalSend;
    };
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Allocation MIS'),
          sheetName: () => getExportFileNameWithDate('Allocation MIS'),
          transformResponse: (payload) => transformAllocMisExport(payload),
        },
      },
      handler: {
        handleFilteredApiResponse: (response) => {
          decorateAllocationMisResponse(response);

          const tableState = tableStateHandlerRef.current?.();
          const previousPagination = currentPaginationRef.current;
          const currentPagination = tableState?.pagination || previousPagination;
          const currentLimit = Number(currentPagination?.limit) || Number(previousPagination?.limit) || PAGE_SIZE;
          const responseData = getAllocationMisResponseData(response);
          const responseMeta = getAllocationMisResponseMeta(response);
          const currentSortSignature = getAllocationMisSortSignature(tableState?.tableConfiguration)
            || getAllocationMisSortSignature(responseData?.tableConfiguration);

          if (responseMeta?.page) {
            currentPaginationRef.current = {
              ...currentPaginationRef.current,
              page: Number(responseMeta.page) || currentPaginationRef.current.page,
              limit: Number(responseMeta.limit) || currentLimit,
              total: Number(responseMeta.total) || currentPaginationRef.current.total,
            };
          } else if (currentPagination?.page) {
            currentPaginationRef.current = {
              ...currentPaginationRef.current,
              ...currentPagination,
            };
          }

          if (currentSortSignature) {
            lastSortSignatureRef.current = currentSortSignature;
          }

          paginationClickInFlightRef.current = false;

          return response;
        },
        setTableStateHandler: (getTableState) => {
          tableStateHandlerRef.current = getTableState;
          const tableState = getTableState?.();
          if (tableState?.pagination?.page) {
            currentPaginationRef.current = {
              ...currentPaginationRef.current,
              ...tableState.pagination,
            };
          }
        },
      },
      headers: {
        token: userProfile.access_token,
      },
      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: <AllocationMisToolbar />,
        showSaveView: false,
        showReset: true,
        showExpandCollapse: false,
        showFilterOption: true,
        rowsPerPageOptions: [],
        showRowsPerPage: false,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: PAGE_SIZE,
      loaderSkeleton: {
        rows: PAGE_SIZE,
        bodyCells: 2,
        columns: 5,
      },
      tableScrollHeight: 'calc(100vh - 260px)',
      paginationConfig: {
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        showPageSizeSelector: false,
      },
      showExpandCollapse: false,
      maxFrozenColumns: {
        desktop: 2,
        tablet: 2,
        mobile: 1,
      },
    },
  }), [userProfile]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 1. Existing Double Click Handler (Keep this as-is)
    const handleDblClick = (event) => {
      const cellContent = event.target.closest('.tu-cell-content');
      if (cellContent) {
        const range = document.createRange();
        range.selectNodeContents(cellContent);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    };

    // 2. NEW: Unified Tooltip Handler for Headers and Data Cells
    const handleMouseOver = (event) => {
      // Looks for either the hidden header cell text or the row data text cell
      const targetElement = event.target.closest('.tu-header-line, .tu-cell-content');

      if (targetElement) {
        // Check if the text is physically truncated/cut off by layout limits
        const isTruncated = targetElement.scrollWidth > targetElement.clientWidth;

        if (isTruncated) {
          // Read the full text string and map it to a standard tooltip popup
          targetElement.setAttribute('title', targetElement.textContent.trim());
        } else {
          // Remove it if the column is wide enough to display the text fully
          targetElement.removeAttribute('title');
        }
      }
    };

    // Bind the events cleanly inside the container element
    container.addEventListener('dblclick', handleDblClick);
    container.addEventListener('mouseover', handleMouseOver);

    // Cleanup: Cleans up listeners during re-renders to prevent memory leaks
    return () => {
      container.removeEventListener('dblclick', handleDblClick);
      container.removeEventListener('mouseover', handleMouseOver);
    };
  }, []);

  return (
    <div ref={containerRef} className="allocation-mis-outer-layer">
      <div className="allocation-mis-card">
        <div className="allocation-mis-table-utility">
          <TableUtility key={tableInstanceKey} configuration={configuration} />
        </div>
      </div>
    </div>
  );
};

export default AllocationMis;
